import React, { useState, useEffect } from 'react';
import { Crown, Trophy, Sparkles, ExternalLink, Calendar, Award } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const BACKEND_URL = window.location.origin;

const WinnerAnnouncement = ({ compact = false }) => {
  const [winner, setWinner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [competition, setCompetition] = useState(null);

  useEffect(() => {
    fetchLatestWinner();
  }, []);

  const fetchLatestWinner = async () => {
    try {
      // Fetch competition winner from backend
      const response = await fetch(`${BACKEND_URL}/api/competition/winner/latest`);
      const data = await response.json();
      
      if (data.winner) {
        setWinner(data.winner);
        setCompetition(data.competition);
      }
    } catch (error) {
      console.error('Error fetching winner:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !winner) {
    return null; // Don't show anything if no winner yet
  }

  if (compact) {
    // Compact banner for header/top of pages
    return (
      <div className="bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-yellow-500/10 border-y border-yellow-500/30 py-3">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-3 text-center flex-wrap">
          <Crown className="w-5 h-5 text-yellow-400 animate-bounce" />
          <span className="text-white font-bold text-sm sm:text-base">
            🎉 Winner: <span className="text-yellow-400">{winner.player_name}</span>
          </span>
          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50 text-xs">
            Score: {winner.score}
          </Badge>
          <span className="text-gray-400 text-xs sm:text-sm">
            {competition?.game_name || 'Sunday Competition'}
          </span>
        </div>
      </div>
    );
  }

  // Full winner announcement card
  return (
    <Card className="bg-gradient-to-br from-yellow-500/20 via-orange-500/20 to-yellow-500/20 border-2 border-yellow-500/50 shadow-2xl shadow-yellow-500/20 animate-fade-in relative overflow-hidden">
      {/* Animated background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-32 h-32 bg-yellow-400/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-32 h-32 bg-orange-400/10 rounded-full blur-3xl animate-pulse delay-700"></div>
      </div>

      <CardContent className="relative pt-8 pb-8">
        {/* Crown Icon */}
        <div className="flex justify-center mb-4">
          <div className="relative">
            <Crown className="w-16 h-16 text-yellow-400 animate-bounce" />
            <Sparkles className="w-6 h-6 text-yellow-300 absolute -top-2 -right-2 animate-pulse" />
          </div>
        </div>

        {/* Announcement Header */}
        <div className="text-center mb-6">
          <Badge className="bg-yellow-500/30 text-yellow-300 border-yellow-500/50 mb-3 text-sm px-4 py-1">
            <Trophy className="w-4 h-4 mr-1 inline" />
            WINNER ANNOUNCEMENT
          </Badge>
          
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-2">
            🎉 Congratulations! 🎉
          </h2>
          
          <p className="text-gray-300 text-base sm:text-lg">
            {competition?.competition_date || 'Sunday'} Competition Winner
          </p>
        </div>

        {/* Winner Details Card */}
        <div className="bg-black/40 backdrop-blur-sm rounded-xl p-6 mb-6 border border-yellow-500/30">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center">
                <Crown className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <p className="text-gray-400 text-xs sm:text-sm">Winner</p>
                <p className="text-white text-xl sm:text-3xl font-black">
                  {winner.player_name}
                </p>
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-gray-400 text-xs sm:text-sm">Winning Score</p>
              <p className="text-yellow-400 text-3xl sm:text-4xl font-black">
                {winner.score}
              </p>
            </div>
          </div>

          {/* Game Info */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="bg-yellow-500/10 rounded-lg p-3 text-center">
              <p className="text-yellow-400 font-bold text-sm mb-1">Game</p>
              <p className="text-white text-xs">{competition?.game_name || 'Competition Game'}</p>
            </div>
            <div className="bg-yellow-500/10 rounded-lg p-3 text-center">
              <p className="text-yellow-400 font-bold text-sm mb-1">Date</p>
              <p className="text-white text-xs">
                {competition?.competition_date 
                  ? new Date(competition.competition_date).toLocaleDateString('en-IN', { 
                      day: 'numeric', 
                      month: 'short' 
                    })
                  : 'Today'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Prize Info */}
        <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/50 rounded-xl p-4 mb-6 text-center">
          <Award className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
          <p className="text-yellow-400 font-bold text-sm mb-1">GRAND PRIZE</p>
          <p className="text-white text-lg font-black">Mobile Phone</p>
          <p className="text-gray-400 text-xs mt-2">
            Winner will be contacted via registered email/phone
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button 
            onClick={() => window.location.href = '/leaderboard'}
            className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-bold"
          >
            <Trophy className="w-4 h-4 mr-2" />
            View Full Leaderboard
          </Button>
          
          <Button 
            onClick={() => window.location.href = '/play-games'}
            variant="outline"
            className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Play Next Competition
          </Button>
        </div>

        {/* Footer Message */}
        <div className="text-center mt-6">
          <p className="text-gray-400 text-xs">
            🎮 Think you can beat this score? Join our next competition!
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default WinnerAnnouncement;
